import React from 'react';
import { FiEdit3 } from 'react-icons/fi';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

const MathTranslationLesson = () => {
  const sections = [
    {
      id: 'words-to-math',
      title: 'Words to Math',
      content: {
        introduction:
          'Many GRE Quant mistakes happen before calculation starts. This lesson teaches how to translate English into math step by step.',
        concepts: [
          {
            title: 'Identify the Unknown',
            prerequisiteNote:
              'You only need to recognize what the question is asking for.',
            vocabulary: ['unknown', 'variable', 'quantity', 'equation'],
            explanation:
              'Start every word problem by naming the unknown. If the problem asks for a number, call it x. If there are two related quantities, define both before writing equations.',
            microExample: {
              problem: 'A number increased by 5 is 18. What is the number?',
              solution: [
                'Let §x§ be the number.',
                'Increased by 5 means §x + 5§.',
                'Equation: §x + 5 = 18§.',
              ],
            },
            greTrap:
              'Do not start calculating until you know exactly what the variable represents.',
            checkQuestion:
              'In “three more than twice a number,” what should the variable represent?',
            example: {
              problem:
                'The sum of twice a number and 7 is 31. What is the number?',
              solution: [
                'Let §x§ be the number.',
                'Twice the number is §2x§.',
                'The sum of twice the number and 7 is §2x + 7§.',
                'Set equation: §2x + 7 = 31§',
                'Solve: §2x = 24§, so §x = 12§.',
              ],
            },
          },
          {
            title: 'Translate Comparison Words',
            prerequisiteNote:
              'Comparison words tell you which operation to use.',
            vocabulary: ['more than', 'less than', 'twice', 'difference', 'ratio'],
            explanation:
              'Translate slowly: “more than” usually adds, “less than” subtracts, “twice” multiplies by 2, and “difference” means subtract two quantities.',
            microExample: {
              problem: 'Write “7 less than a number” in math.',
              solution: [
                'Let the number be §x§.',
                '7 less than the number means §x - 7§.',
              ],
            },
            greTrap:
              '“7 less than x” is §x - 7§, not §7 - x§.',
            checkQuestion: 'Translate “4 more than three times x.”',
            example: {
              problem:
                'A number is 6 less than twice another number y. Write an expression.',
              solution: [
                'Twice the other number is §2y§.',
                '6 less than that is §2y - 6§.',
                'Expression: §2y - 6§.',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'gre-setup',
      title: 'GRE Setup Patterns',
      content: {
        introduction:
          'GRE word problems often hide the same few setups: part-whole, rate-time, percent change, and ratio parts.',
        concepts: [
          {
            title: 'Part-Whole Setup',
            prerequisiteNote:
              'Use this when a problem gives a total and asks for pieces.',
            vocabulary: ['part', 'whole', 'total', 'remaining'],
            explanation:
              'When pieces add to a total, write an equation that adds the parts. If one part is unknown, define it and express the other parts in terms of it.',
            microExample: {
              problem: 'A total of 20 students includes x boys. How many girls?',
              solution: ['Girls = total - boys', 'Girls = §20 - x§'],
            },
            greTrap:
              'Check whether the question asks for the part, the whole, or the remaining amount.',
            checkQuestion:
              'If a total is 75 and one part is x, what expression gives the other part?',
            example: {
              problem:
                'A class has 30 students. There are twice as many girls as boys. How many boys are there?',
              solution: [
                'Let §b§ be boys.',
                'Girls = §2b§.',
                'Total: §b + 2b = 30§.',
                '§3b = 30§, so §b = 10§.',
              ],
            },
          },
          {
            title: 'Min/Max Overlap Setup',
            prerequisiteNote:
              'Use this when two groups are inside the same total population.',
            vocabulary: ['overlap', 'at least', 'at most', 'neither', 'both'],
            explanation:
              'For two groups, start with §A + B - overlap = total in at least one group§. Maximum overlap is the smaller group. Minimum overlap is §A + B - total§ if that is positive, otherwise 0. See Sets & Venn Diagrams for the full diagram method.',
            microExample: {
              problem:
                'In a group of 50, 30 study math and 25 study verbal. What is the minimum possible overlap?',
              solution: [
                'The two group counts add to §30 + 25 = 55§.',
                'Only 50 people exist, so at least §55 - 50 = 5§ people must be in both groups.',
              ],
            },
            greTrap:
              'For minimum overlap, do not subtract the smaller group. Use §A + B - total§.',
            checkQuestion:
              'If two categories add to less than the total, what is the minimum overlap?',
            example: {
              problem:
                'Of 80 applicants, 50 have work experience and 45 have quant experience. What is the least possible number who have both?',
              solution: [
                'Add the category counts: §50 + 45 = 95§.',
                'There are only 80 applicants.',
                'Minimum overlap = §95 - 80 = 15§.',
                'At least 15 applicants must have both.',
              ],
            },
          },
          {
            title: 'Consecutive Integer Translation',
            prerequisiteNote:
              'Consecutive integers are numbers one apart, like 7, 8, 9.',
            vocabulary: ['consecutive', 'integer', 'odd', 'even', 'sum'],
            explanation:
              'Translate consecutive integers as §n, n+1, n+2§. Consecutive even or odd integers move by 2: §n, n+2, n+4§.',
            microExample: {
              problem: 'Write three consecutive integers starting with n.',
              solution: ['They are §n§, §n+1§, and §n+2§.'],
            },
            greTrap:
              'For consecutive odd or even integers, add 2 each time, not 1.',
            checkQuestion:
              'How would you write three consecutive even integers?',
            example: {
              problem:
                'The sum of three consecutive integers is 72. What is the largest?',
              solution: [
                'Let the integers be §n§, §n+1§, and §n+2§.',
                'Equation: §n + (n+1) + (n+2) = 72§.',
                '§3n + 3 = 72§, so §3n = 69§ and §n = 23§.',
                'The largest is §n+2 = 25§.',
              ],
            },
          },
          {
            title: 'Unit Setup',
            prerequisiteNote:
              'Units tell you which quantities can be added, multiplied, or divided.',
            vocabulary: ['unit', 'rate', 'per', 'conversion'],
            explanation:
              'The word “per” means divide. Miles per hour means miles divided by hours. Dollars per item means dollars divided by items.',
            microExample: {
              problem: 'What does 60 miles per 2 hours mean?',
              solution: ['Rate = §60 ÷ 2 = 30§ miles per hour.'],
            },
            greTrap:
              'Never add quantities with different units unless you convert first.',
            checkQuestion: 'What operation does “per” usually signal?',
            example: {
              problem:
                'A printer prints 45 pages in 9 minutes. How many pages per minute?',
              solution: [
                'Pages per minute means pages divided by minutes.',
                '§45 ÷ 9 = 5§',
                'Rate: 5 pages per minute.',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'untimed',
      question: 'Translate: “5 more than twice x.”',
      options: ['§5x + 2§', '§2x + 5§', '§2(x + 5)§', '§x/2 + 5§'],
      correct: 1,
      explanation: 'Twice x is §2x§. 5 more than that is §2x + 5§.',
    },
    {
      type: 'untimed',
      question:
        'A total of 48 tickets were sold. If x were adult tickets, how many were child tickets?',
      options: ['§48x§', '§x - 48§', '§48 - x§', '§48/x§'],
      correct: 2,
      explanation: 'Child tickets are the remaining tickets: §48 - x§.',
    },
    {
      type: 'untimed',
      question: 'Translate: “the product of 7 and n.”',
      options: ['§7 + n§', '§7n§', '§n/7§', '§7 - n§'],
      correct: 1,
      explanation: 'Product means multiply: §7n§.',
    },
    {
      type: 'untimed',
      question:
        'A number increased by 5 is 18. If x is the number, which equation is correct?',
      options: ['§5x = 18§', '§x - 5 = 18§', '§x + 5 = 18§', '§x/5 = 18§'],
      correct: 2,
      explanation: 'Increased by 5 means add 5: §x + 5 = 18§.',
    },
    {
      type: 'untimed',
      question: 'Translate: “12 less than y.”',
      options: ['§12 - y§', '§y - 12§', '§12y§', '§y/12§'],
      correct: 1,
      explanation: '“Less than” subtracts from the named quantity: §y - 12§.',
    },
    {
      type: 'untimed',
      question:
        'A printer prints 45 pages in 9 minutes. Which expression is pages per minute?',
      options: ['§45 × 9§', '§9/45§', '§45/9§', '§45 + 9§'],
      correct: 2,
      explanation: '“Per” means divide: §45 ÷ 9 = 5§ pages per minute.',
    },
    {
      type: 'untimed',
      question:
        'There are 30 students. If b are in group B, how many are not in group B?',
      options: ['§30b§', '§b - 30§', '§30 - b§', '§30/b§'],
      correct: 2,
      explanation: 'The remaining students are the whole minus the part: §30 - b§.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'A number is 4 less than three times y. Which expression represents the number?',
      options: ['§4 - 3y§', '§3y - 4§', '§4y - 3§', '§3(y - 4)§'],
      correct: 1,
      explanation: 'Three times y is §3y§. 4 less than that is §3y - 4§.',
    },
    {
      type: 'timed',
      timeLimit: 80,
      question: 'Translate: “twice the sum of x and 3.”',
      options: ['§2x + 3§', '§2(x + 3)§', '§x + 6§', '§2x + 6§'],
      correct: 1,
      explanation: 'The sum is §x + 3§. Twice that sum is §2(x + 3)§.',
    },
    {
      type: 'timed',
      timeLimit: 85,
      question:
        'Adult tickets cost $12 and child tickets cost $8. If a adults and c children buy tickets, which expression is the total cost?',
      options: ['§12a + 8c§', '§20(a + c)§', '§12a - 8c§', '§a + c + 20§'],
      correct: 0,
      explanation: 'Each adult adds 12 and each child adds 8: §12a + 8c§.',
    },
  ];

  const quickReference = [
    'Name the unknown before calculating',
    '“More than” adds; “less than” subtracts',
    '“Twice” = multiply by 2',
    '“Per” usually means divide',
    'Part + part = whole',
    'Minimum overlap = group A + group B - total (if positive)',
    'Consecutive integers: n, n+1, n+2; consecutive evens/odds: n, n+2, n+4',
  ];

  return (
    <LessonTemplate
      lessonId="math-translation"
      lessonTitle="Math Translation"
      lessonIcon={<FiEdit3 />}
      lessonDescription="Translate GRE word problems into variables, expressions, and equations"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={{
        title: 'Need help translating word problems?',
        spaceName: 'Quant hacks',
        humanCoachTitle: 'Talk to a GRE coach',
        liwazaAITitle: 'Ask AI',
        communityTitle: 'Ask other students',
        showHumanCoach: true,
        showLiwazaAI: true,
        showCommunity: true,
        aifirstmessage:
          'I need help translating GRE Quant word problems into equations',
        aicontext:
          'Help a beginner GRE Quant learner define variables, translate words to operations, and set up equations.',
      }}
      supplementalPracticeSlot={<QuantLessonPracticeSlot lessonId="math-translation" />}
    />
  );
};

export default MathTranslationLesson;
